import moment from "moment";

// Excel date helpers: write real date cells (not text) so Excel's column filter
// groups the values by Year > Month > Day.

export const EXCEL_DATE_FORMAT = "dd/mm/yyyy";
export const EXCEL_DATE_TIME_FORMAT = "dd/mm/yyyy hh:mm";

// Parse `value` with moment. `inputFormat` is only needed when `value` is an already
// formatted string (e.g. "MM-DD-YYYY"), otherwise raw API values (ISO / Date) are parsed as is.
const parseDate = (value, inputFormat) => {
  if (!value || value === "-") return null;
  const date = inputFormat ? moment(value, inputFormat, true) : moment(value);
  return date.isValid() ? date : null;
};

// Excel serial day number for the calendar date of `value` (timezone safe). null when empty/invalid.
export const toExcelDate = (value, inputFormat) => {
  const date = parseDate(value, inputFormat);
  if (!date) return null;
  return (Date.UTC(date.year(), date.month(), date.date()) - Date.UTC(1899, 11, 30)) / 86400000;
};

// Same as toExcelDate but keeps the time of day as the fractional part.
export const toExcelDateTime = (value, inputFormat) => {
  const date = parseDate(value, inputFormat);
  if (!date) return null;
  return (
    (Date.UTC(date.year(), date.month(), date.date(), date.hours(), date.minutes(), date.seconds()) -
      Date.UTC(1899, 11, 30)) /
    86400000
  );
};

// SheetJS (xlsx / xlsx-js-style): a date cell object that can be placed directly in the
// rows passed to aoa_to_sheet / json_to_sheet. Returns "-" when empty/invalid.
export const excelDateCell = (value, inputFormat) => {
  const serial = toExcelDate(value, inputFormat);
  return serial === null ? "-" : { t: "n", v: serial, z: EXCEL_DATE_FORMAT };
};

export const excelDateTimeCell = (value, inputFormat) => {
  const serial = toExcelDateTime(value, inputFormat);
  return serial === null ? "-" : { t: "n", v: serial, z: EXCEL_DATE_TIME_FORMAT };
};

// CSV has no cell types: ISO yyyy-mm-dd is the only text Excel reads as a real date in every locale.
export const csvDate = (value, inputFormat) => {
  const date = parseDate(value, inputFormat);
  return date ? date.format("YYYY-MM-DD") : "-";
};

export const csvDateTime = (value, inputFormat) => {
  const date = parseDate(value, inputFormat);
  return date ? date.format("YYYY-MM-DD HH:mm") : "-";
};

// SheetJS: enable the auto filter over the whole used range, starting at the header row.
export const setSheetJsAutoFilter = (XLSX, worksheet, headerRowIndex = 0) => {
  if (!worksheet || !worksheet["!ref"]) return worksheet;
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  worksheet["!autofilter"] = {
    ref: XLSX.utils.encode_range({ s: { r: headerRowIndex, c: range.s.c }, e: { r: range.e.r, c: range.e.c } }),
  };
  return worksheet;
};

// ExcelJS: set a cell to a date value (or "-" when empty)
export const setExcelJsDateCell = (row, columnNumber, value, inputFormat) => {
  const serial = toExcelDate(value, inputFormat);
  const cell = row.getCell(columnNumber);
  if (serial === null) {
    cell.value = "-";
    return;
  }
  cell.value = serial;
  cell.numFmt = EXCEL_DATE_FORMAT;
};

// ExcelJS: set a cell to a date + time value (or "-" when empty)
export const setExcelJsDateTimeCell = (row, columnNumber, value, inputFormat) => {
  const serial = toExcelDateTime(value, inputFormat);
  const cell = row.getCell(columnNumber);
  if (serial === null) {
    cell.value = "-";
    return;
  }
  cell.value = serial;
  cell.numFmt = EXCEL_DATE_TIME_FORMAT;
};

// ExcelJS: enable the auto filter from the header row down to the last data row
export const setExcelJsAutoFilter = (worksheet, headerRow, lastDataRowNumber, columnCount) => {
  if (!worksheet || !headerRow) return;
  worksheet.autoFilter = {
    from: { row: headerRow.number, column: 1 },
    to: { row: Math.max(lastDataRowNumber || headerRow.number, headerRow.number), column: columnCount },
  };
};

// SheetJS (xlsx): convert the given columns (0 based) of an aoa worksheet into date cells
// and enable the auto filter on the header row.
export const applySheetJsDates = (XLSX, worksheet, dateColumns = [], headerRowIndex = 0) => {
  if (!worksheet || !worksheet["!ref"]) return worksheet;
  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  for (let r = headerRowIndex + 1; r <= range.e.r; r++) {
    for (const c of dateColumns) {
      const address = XLSX.utils.encode_cell({ r, c });
      const cell = worksheet[address];
      if (!cell || cell.v === "-" || cell.v === "" || cell.v === null || cell.v === undefined) continue;
      const parsed = moment(cell.v, ["DD/MM/YYYY", "DD/MM/YY", "DD-MM-YYYY", moment.ISO_8601], true);
      if (!parsed.isValid()) continue;
      worksheet[address] = { t: "n", v: toExcelDate(parsed), z: EXCEL_DATE_FORMAT };
    }
  }

  worksheet["!autofilter"] = {
    ref: XLSX.utils.encode_range({ s: { r: headerRowIndex, c: range.s.c }, e: { r: range.e.r, c: range.e.c } }),
  };
  return worksheet;
};
